/**
 * Check if a value is a plain object (not Date/Map/Set/class instance).
 */
const isPlainObject = (v: unknown): v is Record<string, unknown> => (
  Object.prototype.toString.call(v) === '[object Object]'
);

/**
 * Convert a single key from snake_case or kebab-case to camelCase.
 *
 * @param {string} key - The original key.
 * @returns {string} - The camelCase version of the key.
 *
 * @example toCamelCaseKey('created_at') // "createdAt"
 * @example toCamelCaseKey('user-name')  // "userName"
 */
function toCamelCaseKey(key: string): string {
  return key
    .replace(/[_-]+(\w)/g, (_, c: string) => c.toUpperCase())
    .replace(/^\w/, c => c.toLowerCase());
}

/**
 * Recursively converts all object keys from snake_case or kebab-case to camelCase.
 *
 * - Arrays are processed element by element.
 * - Primitives are returned as-is.
 * - Non-plain objects (Date, Map, Set, class instances) are returned untouched.
 * - Input is not mutated; a new object/array is returned.
 *
 * Overloads are defined for clearer inference at call sites.
 */
export function keysToCamel(input: null | undefined): null | undefined;
export function keysToCamel(input: string | number | boolean | symbol | bigint): typeof input;
export function keysToCamel(input: unknown[]): unknown[];
export function keysToCamel(input: Record<string, unknown>): Record<string, unknown>;
export function keysToCamel(input: unknown): unknown; // catch-all for unknown-typed inputs

/**
 * @param {unknown} input - Any value.
 * @returns {unknown} A new value with all object keys converted to camelCase.
 */
export function keysToCamel(input: unknown): unknown {
  if (input == null) {
    return input;
  }

  if (Array.isArray(input)) {
    return input.map((item) => keysToCamel(item));
  }

  if (isPlainObject(input)) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(input)) {
      out[toCamelCaseKey(k)] = keysToCamel(v);
    }
    return out;
  }

  return input;
}
